import { Fragment, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { InformationCircleIcon } from "@heroicons/react/outline";
import renderMathInElement from "../../utils/auto-render";

type DmAlertModalProps = {
  showModal: boolean;
  setShowModal: React.Dispatch<boolean>;
  headline: string;
  message: any;
  buttonText?: string;
  modalAction: any;
  showButton?: boolean;
};

export default function DmAlertModal({
  showModal,
  setShowModal,
  headline,
  message,
  buttonText,
  modalAction,
  showButton = true,
}: DmAlertModalProps): JSX.Element {
  const messageRef = useRef(null);

  return (
    <Transition.Root show={showModal} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={setShowModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-60 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              beforeEnter={() => renderMathInElement(messageRef.current)}
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div>
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#def0f6]">
                    <InformationCircleIcon
                      className="h-6 w-6 text-dm-alt-blue"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      {headline}
                    </Dialog.Title>
                    <div className="mt-2">
                      <Dialog.Description
                        ref={messageRef}
                        className="text-md text-gray-700"
                        role="alertdialog"
                      >
                        {message}
                      </Dialog.Description>
                    </div>
                  </div>
                </div>
                {showButton && (
                  <div className="mt-5 sm:mt-6">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-dm-blue px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-dm-dark-blue focus:outline-none focus:ring-2 focus:ring-dm-lightest-blue focus:ring-offset-2 sm:text-sm"
                      onClick={() => {
                        setShowModal(false);
                        modalAction();
                      }}
                    >
                      {buttonText || "Okay"}
                    </button>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
